import { Theme } from '@logto/schemas';
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SignInExperienceResponse, Platform } from '@/types';
import { isIdentityConsole } from '@/utils/cookies';

import { subscribeToPortalTheme } from '../../../../toolkit/core-kit/src/browser/portal-theme';

import type { PageContextType } from './PageContext';
import MainContext from './PageContext';

type Props = {
  readonly children: React.ReactNode;
  readonly preset?: Partial<
    Pick<
      PageContextType,
      | 'theme'
      | 'toast'
      | 'loading'
      | 'platform'
      | 'termsAgreement'
      | 'experienceSettings'
      | 'isPreview'
    >
  >;
};

const getInitialTheme = () => {
  if (document.documentElement.dataset.theme === Theme.Dark) {
    return Theme.Dark;
  }

  return Theme.Light;
};

const getInitialPlatform = (): Platform =>
  document.body.classList.contains('mobile') ? 'mobile' : 'web';

const PageContextProvider = ({ children, preset }: Props) => {
  const [searchParameters] = useSearchParams();

  const [loading, setLoading] = useState(preset?.loading ?? false);
  const [toast, setToast] = useState(preset?.toast ?? '');
  const [theme, setTheme] = useState<Theme>(preset?.theme ?? getInitialTheme);

  const [portalTheme, setPortalTheme] = useState<Theme>();
  useEffect(() => {
    if (isIdentityConsole) {
      return subscribeToPortalTheme(process.env.NODE_ENV === 'development', (value) => {
        setPortalTheme(value === 'dark' ? Theme.Dark : Theme.Light);
      });
    }
  }, []);

  const [platform, setPlatform] = useState<Platform>(preset?.platform ?? getInitialPlatform);
  const [termsAgreement, setTermsAgreement] = useState(preset?.termsAgreement ?? false);
  const [experienceSettings, setExperienceSettings] = useState<
    SignInExperienceResponse | undefined
  >(preset?.experienceSettings ?? undefined);

  const isPreview = searchParameters.get('preview') === 'true';

  const pageContext = useMemo<PageContextType>(
    () => ({
      theme: portalTheme ?? theme,
      toast,
      loading,
      platform,
      termsAgreement,
      experienceSettings,
      isPreview,
      setTheme,
      setLoading,
      setToast,
      setPlatform,
      setTermsAgreement,
      setExperienceSettings,
    }),
    [experienceSettings, isPreview, loading, platform, portalTheme, termsAgreement, theme, toast]
  );

  return <MainContext.Provider value={pageContext}>{children}</MainContext.Provider>;
};

export default PageContextProvider;
